import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformServer} from '@angular/common';
import {ActivatedRouteSnapshot, RouterStateSnapshot, Router} from '@angular/router';
import {Observable} from 'rxjs';
import {TokenDataProviderService} from "../services/token-data-provider.service";

@Injectable({
  providedIn: 'root'
})
export class RegistrationCompletedGuard {

  private readonly publicPrefixes = ['/feed', '/events', '/marketplace', '/posts', '/ranking', '/support', '/vehicles'];
  private readonly publicRouteNames = ['feed', 'events', 'marketplace', 'posts', 'ranking', 'support', 'vehicles', 'garage/:id'];

  constructor(
    private router: Router,
    private ts: TokenDataProviderService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
  }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    if (!this.isLoggedIn()) {
      const currentUrl = state.url || this.router.url || '';
      if (this.isPublicUrl(currentUrl) || this.isPublicRoute(next) || this.isPublicUserResourceUrl(currentUrl)) {
        return true;
      }
      this.router.navigate(['/']);
      return false;
    }

    if (this.ts.requiresRegistrationCompletion()) {
      this.router.navigate(['/registration/complete']);
      return false;
    }

    return true;
  }

  private isLoggedIn(): boolean {
    if (isPlatformServer(this.platformId)) {
      return this.ts.isLoggedInSSR();
    }

    return this.ts.isLoggedIn();
  }

  private isPublicUrl(url: string): boolean {
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
    if (/^\/places\/[^/?#]+\/?(?:[?#].*)?$/.test(normalizedUrl)) { return true; }
    return this.publicPrefixes.some((prefix) => normalizedUrl === prefix || normalizedUrl.startsWith(`${prefix}/`));
  }

  private isPublicRoute(next: ActivatedRouteSnapshot): boolean {
    const firstChildPath = next.firstChild?.routeConfig?.path;
    if (!firstChildPath) {
      return false;
    }
    return this.publicRouteNames.some((name) => firstChildPath === name || firstChildPath.startsWith(`${name}/`));
  }

  private isPublicUserResourceUrl(url: string): boolean {
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
    const segments = normalizedUrl.split('/').filter(Boolean);

    if (segments.length < 2) {
      return false;
    }

    if (segments[1] === 'profile') {
      return true;
    }

    if (segments[1] === 'garage' && segments.length >= 3) {
      return true;
    }

    return segments[1] === 'collaborations' && segments.length >= 3;
  }
}
